import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './CookieChoices.css';

const storageKey = 'foodii-website-consent-v1';
const consentLifetime = 180 * 24 * 60 * 60 * 1000;
const analyticsId = 'G-FGFCB9TEGT';
const adClient = 'ca-pub-6495767992135010';
const noOptionalServices = { analytics: false, advertising: false };
const allOptionalServices = { analytics: true, advertising: true };

function readSavedChoice() {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey));
    if (
      typeof saved?.analytics === 'boolean' &&
      typeof saved?.advertising === 'boolean' &&
      typeof saved?.savedAt === 'number' &&
      saved.savedAt <= Date.now() &&
      Date.now() - saved.savedAt < consentLifetime
    ) {
      return { analytics: saved.analytics, advertising: saved.advertising };
    }
  } catch (_) {
    // Browsers that block storage simply ask again on the next visit.
  }
  return null;
}

function addScript(id, src, configure) {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = true;
  configure?.(script);
  document.head.appendChild(script);
}

function startOptionalServices(choice) {
  if (choice.analytics) {
    if (!document.getElementById('foodii-google-analytics')) {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () { window.dataLayer.push(arguments); };
      window.gtag('consent', 'default', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: choice.advertising ? 'granted' : 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
      window.gtag('js', new Date());
      window.gtag('config', analyticsId);
      addScript('foodii-google-analytics', `https://www.googletagmanager.com/gtag/js?id=${analyticsId}`);
    } else {
      window.gtag?.('consent', 'update', { ad_storage: choice.advertising ? 'granted' : 'denied' });
    }
  }

  if (choice.advertising) {
    // Site ads are non-personalized. Google's own advertising controls may still appear.
    window.adsbygoogle = window.adsbygoogle || [];
    window.adsbygoogle.requestNonPersonalizedAds = 1;
    addScript('foodii-funding-choices', `https://fundingchoicesmessages.google.com/i/pub-6495767992135010?ers=1`);
    if (!window.frames.googlefcPresent) {
      const frame = document.createElement('iframe');
      frame.name = 'googlefcPresent';
      frame.hidden = true;
      document.body.appendChild(frame);
    }
    addScript('foodii-adsense', `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adClient}`, (script) => {
      script.crossOrigin = 'anonymous';
      script.setAttribute('data-privacy-treatments', 'disablePersonalization');
    });
  }
}

function clearAnalyticsCookies() {
  const domain = window.location.hostname.endsWith('foodii.co.uk') ? '; domain=.foodii.co.uk' : '';
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    if (!/^(?:_ga|_gid$|_gat|_gac)/.test(name)) return;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    if (domain) document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
  });
}

export default function CookieChoices({ showSettings, onCloseSettings }) {
  const [choice, setChoice] = useState(readSavedChoice);
  const [showDetails, setShowDetails] = useState(false);
  const [draft, setDraft] = useState(choice || noOptionalServices);
  const panel = useRef(null);
  const isOpen = choice === null || showSettings;

  useEffect(() => {
    if (choice) startOptionalServices(choice);
  }, [choice]);

  useEffect(() => {
    if (showSettings) {
      setDraft(choice || noOptionalServices);
      setShowDetails(true);
    }
  }, [showSettings, choice]);

  useEffect(() => {
    if (!isOpen || !panel.current) return;
    const previousPadding = document.body.style.paddingBottom;
    const observer = new ResizeObserver(() => {
      document.body.style.paddingBottom = `${panel.current.getBoundingClientRect().height + 32}px`;
    });
    observer.observe(panel.current);
    return () => {
      observer.disconnect();
      document.body.style.paddingBottom = previousPadding;
    };
  }, [isOpen]);

  function save(next) {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ ...next, savedAt: Date.now() }));
    } catch (_) {
      // The choice still applies for this visit.
    }
    const revoked = (choice?.analytics && !next.analytics) || (choice?.advertising && !next.advertising);
    if (revoked) {
      window.gtag?.('consent', 'update', {
        analytics_storage: next.analytics ? 'granted' : 'denied',
        ad_storage: next.advertising ? 'granted' : 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
      if (!next.analytics) clearAnalyticsCookies();
      window.location.reload();
      return;
    }
    setChoice(next);
    setShowDetails(false);
    onCloseSettings();
  }

  if (!isOpen) return null;

  return (
    <section className="cookie-choice" ref={panel} aria-labelledby="cookie-choice-title">
      <div className="cookie-choice__intro">
        <span className="cookie-choice__eyebrow">FOODII · PRIVACY</span>
        <h2 id="cookie-choice-title">Your privacy, your choice</h2>
        <p>
          We use optional Google Analytics and non-personalized Google ads. Choose what to allow;
          these services wait for your choice. Read our{' '}
          <Link to="/privacy_policy">Privacy Policy</Link>.
        </p>
      </div>

      {showDetails && (
        <div className="cookie-choice__details">
          <div className="cookie-choice__essential">
            <strong>Essential preference</strong>
            <span>Stores your choice on this device. Always on.</span>
          </div>
          <label className="cookie-choice__option">
            <span><strong>Website analytics</strong><small>Google Analytics helps us understand visits.</small></span>
            <input
              type="checkbox"
              checked={draft.analytics}
              onChange={(event) => setDraft({ ...draft, analytics: event.target.checked })}
            />
          </label>
          <label className="cookie-choice__option">
            <span><strong>Website advertising</strong><small>Google may use cookies for non-personalized ads and may ask for further choices.</small></span>
            <input
              type="checkbox"
              checked={draft.advertising}
              onChange={(event) => setDraft({ ...draft, advertising: event.target.checked })}
            />
          </label>
        </div>
      )}

      <div className="cookie-choice__actions">
        <button type="button" className="cookie-choice__button cookie-choice__button--decision" onClick={() => save(noOptionalServices)}>
          Reject optional
        </button>
        {showDetails ? (
          <button type="button" className="cookie-choice__button cookie-choice__button--neutral" onClick={() => save(draft)}>
            Save choices
          </button>
        ) : (
          <button type="button" className="cookie-choice__button cookie-choice__button--neutral" onClick={() => setShowDetails(true)}>
            Manage choices
          </button>
        )}
        <button type="button" className="cookie-choice__button cookie-choice__button--decision" onClick={() => save(allOptionalServices)}>
          Allow optional
        </button>
      </div>
    </section>
  );
}
